import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
} from "reactstrap";
import {
  inputChangeValue,
  nextStep,
  prevStep,
  finishStep,
  clearData,
} from "../../store/settings/ImportExport/actions";
import {
  uploadImportData,
  uploadTablePreviewData,
} from "../../store/settings/ImportExport/actions";
import { getImportModelsList } from "../../store/settings/ImportExport/actions";

const decodeHtml = (html) => {
  if (typeof html !== "string") return html;
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

const ImportCSV = (props) => {
  const [fileData, setFileData] = useState(null);

  useEffect(() => {
    props.clearData();
    props.getImportModelsList();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileData(file);
      props.inputChangeValue({
        key: "import_file",
        value: file,
      });
    }
  };

  const handleModelChange = (e) => {
    const value = e.target.value;
    props.inputChangeValue({
      key: "model_name",
      value: value,
    });
  };

  const handleUpload = () => {
    if (!props.exportData.model_name || !fileData) {
      alert("Please select both model and file");
      return;
    }

    const formData = new FormData();
    formData.append("model_name", props.exportData.model_name);
    formData.append("import_file", fileData);
    formData.append("input_format", 0);

    props.uploadImportData({
      obj: formData,
    });
  };

  const handleSubmit = () => {
    if (props.importData.has_errors || props.importData.has_validation_errors) {
      alert("Cannot submit data with errors");
      return;
    }

    const payload = {
      model_name: props.exportData.model_name,
      request_id: props.request_id,
    };

    props.uploadTablePreviewData({
      obj: payload,
    });
    props.nextStep();
  };

  const handleNext = () => {
    props.nextStep();
  };

  const handlePrevious = () => {
    props.prevStep();
  };

  const handleFinish = () => {
    props.finishStep();
  };

  return (
    <Card>
      <CardBody>
        <h4 className="mb-4">Import CSV</h4>
        <hr />

        {/* STEP 0: UPLOAD */}
        {props.currentStep === 0 && (
          <Form>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="model_name">Select Model Type</Label>
                  <Input
                    type="select"
                    name="model_name"
                    id="model_name"
                    onChange={handleModelChange}
                    value={props.exportData.model_name || ""}
                  >
                    <option value="">Select...</option>
                    {props.importModelList?.map((item, index) => (
                      <option key={index} value={item}>
                        {item}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="import_file">Upload CSV File</Label>
                  <Input
                    type="file"
                    name="import_file"
                    id="import_file"
                    accept=".csv"
                    onChange={handleFileChange}
                  />
                </FormGroup>
                {fileData && (
                  <div className="mt-2">
                    <small>
                      <strong>File:</strong> {fileData.name}
                    </small>
                  </div>
                )}
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Button
                  color="primary"
                  disabled={props.loading}
                  onClick={handleUpload}
                >
                  {props.loading ? "Uploading..." : "Upload & Validate"}
                </Button>
              </Col>
            </Row>
          </Form>
        )}

        {/* STEP 1: PREVIEW */}
        {props.currentStep === 1 && (
          <div>
            {props.importData.has_errors ? (
              <Alert color="danger">
                <strong>Upload Failed!</strong> Please check the errors below.
              </Alert>
            ) : (
              <Alert color="warning">
                <strong>Review Data</strong> Before submitting, please verify
                the data below.
              </Alert>
            )}

            <div className="table-responsive mt-3">
              <table className="table table-bordered table-sm">
                <thead>
                  <tr>
                    {props.importTableColumns?.map((col, idx) => (
                      <th key={idx}>{col.label || col.key || col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {props.importTableRowData?.map((row, idx) => (
                    <tr key={idx}>
                      {props.importTableColumns?.map((col, colIdx) => {
                        const cellValue = row[col.key || col] || "-";
                        const decodedValue = typeof cellValue === "string" ? decodeHtml(cellValue) : cellValue;
                        return (
                          <td key={colIdx}>
                            {decodedValue}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Row className="mt-3">
              <Col md={12}>
                <Button
                  color="secondary"
                  onClick={handlePrevious}
                  className="me-2"
                >
                  Previous
                </Button>
                <Button
                  color="primary"
                  disabled={
                    props.loading ||
                    props.importData.has_errors ||
                    props.importData.has_validation_errors
                  }
                  onClick={handleSubmit}
                >
                  {props.loading ? "Processing..." : "Submit"}
                </Button>
              </Col>
            </Row>
          </div>
        )}

        {/* STEP 2: FINISH */}
        {props.currentStep === 2 && (
          <div className="text-center">
            {props.submit_ImportData?.has_errors ? (
              <Alert color="danger">
                <h5>Import Failed</h5>
                <p>{props.submit_ImportData?.error_message}</p>
              </Alert>
            ) : (
              <Alert color="success">
                <h5>Import Successful!</h5>
                <p>{props.submit_ImportData?.success_message || "Data imported successfully!"}</p>
              </Alert>
            )}

            <Button
              color="primary"
              onClick={handleFinish}
              className="mt-3"
            >
              Done
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

function mapStateToProps({ importExportReducer }) {
  return {
    exportData: importExportReducer.exportData,
    currentStep: importExportReducer.currentStep,
    importData: importExportReducer.importData,
    importTableColumns: importExportReducer.importTableColumns,
    importTableRowData: importExportReducer.importTableRowData,
    submit_ImportData: importExportReducer.submit_ImportData,
    loading: importExportReducer.loading,
    importType: importExportReducer.importType,
    importModelList: importExportReducer.importModelList,
    request_id: importExportReducer.request_id,
  };
}

export default connect(mapStateToProps, {
  getImportModelsList,
  inputChangeValue,
  uploadImportData,
  uploadTablePreviewData,
  nextStep,
  prevStep,
  finishStep,
  clearData,
})(ImportCSV);
